import { HardHat } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { cn, getInitials } from "@/lib/utils";
import { buildTransformedUrl } from "@/lib/images";

// §A3.1 avatar sizing tokens (--avatar-sm/md/lg/xl) → Tailwind size class +
// §A5.2 monogram font size. The CDN transform requests the DISPLAY px; the
// helper applies the house dpr=2 (§I6) so retina is handled centrally.
const SIZES = {
  sm: { px: 24, box: "size-6", text: "text-[10px]" }, // --avatar-sm: inline byline / chip
  md: { px: 32, box: "size-8", text: "text-xs" }, //    --avatar-md: list rows (default)
  lg: { px: 48, box: "size-12", text: "text-base" }, // --avatar-lg: cards / crew panels
  xl: { px: 96, box: "size-24", text: "text-3xl" }, //  --avatar-xl: detail hero
} as const;

type RepAvatarSize = keyof typeof SIZES;

interface RepAvatarProps {
  /** Rep's display name — drives the monogram fallback (§A5.1) and the alt/title. */
  name?: string | null;
  /** Stored headshot `public_url`; face-cropped + dpr=2'd through the §I6 helper. */
  headshotUrl?: string | null;
  size?: RepAvatarSize;
  className?: string;
}

/**
 * The one standard rep renderer (ADV-1354). A shadcn `Avatar` showing a
 * face-cropped headshot — §A4.1 (team members always prefer the real photo) +
 * §I6 `gravity=face` so a full-body shot crops to the face, not the torso — with
 * a deterministic fallback chain (§A9.10: never a monogram when a headshot
 * exists). Radix `AvatarImage` auto-falls-back to `AvatarFallback` on a
 * missing/failed `src`, so no manual `onError` is needed (§I7.2 — never a broken
 * image). Fallback is the monogram (§A5.1, first+last initial); when there is no
 * name either, the team-member entity glyph (§A6.2 hard-hat), not "??" — an
 * empty monogram would imply an unknown human.
 *
 * One shared renderer so every rep surface (team-members list/detail, board-card
 * assignee, rep table columns) is visually consistent.
 */
export function RepAvatar({
  name,
  headshotUrl,
  size = "md",
  className,
}: RepAvatarProps) {
  const { px, box, text } = SIZES[size];
  const src = buildTransformedUrl(headshotUrl, {
    width: px,
    height: px,
    fit: "cover",
    gravity: "face",
  });
  const initials = getInitials(name);

  return (
    <Avatar
      className={cn(box, "shrink-0", className)}
      title={name ?? undefined}
    >
      {src && <AvatarImage src={src} alt={name ?? ""} />}
      <AvatarFallback className="bg-[color:var(--color-surface)] text-[color:var(--color-text-md)] typo-mono uppercase">
        {initials ? (
          <span className={text}>{initials}</span>
        ) : (
          <HardHat className="size-[45%]" aria-hidden />
        )}
      </AvatarFallback>
    </Avatar>
  );
}
